import React, { useState } from "react";
import { ICONS } from "../../../utils/fontawesome";
import { IconButton } from "./ReservationList";

const AddReservationCard = ({
  disabled = false,
  ateliers = [],
  onCreate,
  creneau = null,
  loading = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [atelierId, setAtelierId] = useState("");
  const [participants, setParticipants] = useState(1);
  const [client, setClient] = useState({
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
  });
  const [commentaire, setCommentaire] = useState("");
  const placesDisponibles = Number(creneau?.places);
  const maxParticipants =
    Number.isFinite(placesDisponibles) && placesDisponibles > 0
      ? placesDisponibles
      : 8;

  React.useEffect(() => {
    setParticipants((prev) =>
      Math.max(1, Math.min(prev || 1, maxParticipants || 1))
    );
  }, [maxParticipants]);

  const isFormComplete =
    Boolean(atelierId) &&
    Number.isFinite(participants) &&
    participants > 0 &&
    client.nom.trim() &&
    client.prenom.trim() &&
    client.email.trim() &&
    client.telephone.trim();
  const createDisabled = disabled || !isFormComplete;

  return (
    <div className="border border-dashed border-slate-300 rounded-lg p-4 flex flex-col gap-4 text-slate-600">
      <div className="flex items-center justify-between gap-3">
        <span className="font-medium select-none">Ajouter un créneau</span>
        <IconButton
          label={expanded ? "Fermer le formulaire" : "Ajouter un créneau"}
          icon={expanded ? ICONS.close : ICONS.plus}
          onClick={disabled ? undefined : () => setExpanded((prev) => !prev)}
          disabled={disabled}
          className="text-orange-600 border-orange-300 hover:text-orange-700 hover:border-orange-400 bg-white"
        />
      </div>

      {expanded && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Atelier</label>
              <select
                value={atelierId}
                onChange={(e) => setAtelierId(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-200"
              >
                <option value="">Sélectionner un atelier</option>
                {ateliers.map((a) => (
                  <option key={a.atelier_id ?? a.id} value={a.atelier_id ?? a.id}>
                    {a.nom}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">Participants</label>
                <span className="text-xs text-slate-500">max {maxParticipants}</span>
              </div>
              <input
                type="number"
                min={1}
                max={maxParticipants}
                value={participants}
                onChange={(e) => setParticipants(Number(e.target.value))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-200"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-700">Commentaire</label>
            <textarea
              rows={3}
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-200"
              placeholder="Informations complémentaires (optionnel)"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Nom</label>
              <input
                type="text"
                value={client.nom}
                onChange={(e) => setClient((prev) => ({ ...prev, nom: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-200"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Prénom</label>
              <input
                type="text"
                value={client.prenom}
                onChange={(e) => setClient((prev) => ({ ...prev, prenom: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-200"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Email</label>
              <input
                type="email"
                value={client.email}
                onChange={(e) => setClient((prev) => ({ ...prev, email: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-200"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Téléphone</label>
              <input
                type="tel"
                value={client.telephone}
                onChange={(e) => setClient((prev) => ({ ...prev, telephone: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-200"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                createDisabled || loading
                  ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                  : "bg-orange-600 text-white hover:bg-orange-700"
              }`}
              disabled={createDisabled || loading}
              onClick={() => {
                if (createDisabled || loading) return;
                if (typeof onCreate === "function") {
                  onCreate({
                    atelierId,
                    participants,
                    commentaire,
                    client,
                  });
                }
              }}
            >
              {loading ? "Création..." : "Créer une réservation"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddReservationCard;
